import { ReportFormat } from '@drivingdoc/shared-types/reports';
import axios from 'axios';

import { privateApi } from 'api';

/**
 * Format metadata for report generation
 */
const FORMAT_METADATA: Record<ReportFormat, { mimeType: string; fileExtension: string }> = {
  csv: { mimeType: 'text/csv', fileExtension: '.csv' },
  xlsx: {
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    fileExtension: '.xlsx',
  },
  pdf: { mimeType: 'application/pdf', fileExtension: '.pdf' },
};

export interface GenerateReportParams {
  reportId: string;
  format: ReportFormat;
  parameters: Record<string, unknown>;
}

async function generateReport(params: GenerateReportParams): Promise<void> {
  const { reportId, format, parameters } = params;
  const formatMeta = FORMAT_METADATA[format];

  // Get the user's timezone
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  try {
    const result = await privateApi.post(
      `${import.meta.env.VITE_API_BASE_URL}/reports/${reportId}/generate`,
      { format, parameters, timezone },
      {
        responseType: 'blob',
        headers: {
          Accept: formatMeta.mimeType,
        },
      },
    );

    // Extract filename from Content-Disposition header
    const contentDisposition = result.headers['content-disposition'];
    const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
    const fileName = filenameMatch?.[1] ?? `report${formatMeta.fileExtension}`;

    // Create download link
    const url = window.URL.createObjectURL(new Blob([result.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    // Handle error responses that come as blobs
    if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
      const text = await error.response.data.text();
      try {
        const errorData = JSON.parse(text);
        throw new Error(errorData.error?.message ?? errorData.message ?? 'Failed to generate report');
      } catch (parseError) {
        // If parsing fails, throw the original text
        if (parseError instanceof SyntaxError) {
          throw new Error(text || 'Failed to generate report');
        }
        throw parseError;
      }
    }
    throw error;
  }
}

export { generateReport };
